import React, { createContext, useContext, useState, useEffect } from 'react';
import { signInWithPopup, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import api from '../config/api';

const AuthContext = createContext();

export const ADMIN_EMAILS = [
  'gauravkhandekar007@gmail.com',
  'beatsofredmond@gmail.com',
  'beatsofwashington@gmail.com',
  'admin@bow.org'
];

export const isUserAdmin = (user) => {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  const email = (user.email || '').toLowerCase();
  return role === 'admin' || user.isAdmin === true || ADMIN_EMAILS.includes(email);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  // Normalize user with role checks
  const normalizeUser = (user) => {
    if (!user) return null;
    const email = (user.email || '').toLowerCase();
    const isAdmin = isUserAdmin(user);
    return {
      ...user,
      role: isAdmin ? 'admin' : (user.role || 'member'),
      isAdmin: isAdmin
    };
  };

  // Sync Google user with backend DynamoDB
  const syncUserWithBackend = async (firebaseUser) => {
    try {
      // Check for existing user in DynamoDB backend
      let res;
      try {
        res = await api.user.get(`/users/email/${encodeURIComponent(firebaseUser.email)}`);
      } catch (e) {}
      if (!res || !res.ok) {
        try {
          res = await api.get(`/users/email/${encodeURIComponent(firebaseUser.email)}`);
        } catch (e) {}
      }

      if (res && res.ok) {
        const userData = await res.json();
        const userObj = userData.user || userData;
        return { user: normalizeUser(userObj) };
      }
    } catch (error) {
      // User not found in backend yet
    }

    // Create user in DynamoDB via Google endpoint
    try {
      const googleUserData = {
        firebaseUid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || '',
        firstName: firebaseUser.displayName?.split(' ')[0] || '',
        lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
        phone: firebaseUser.phoneNumber || '',
        photoURL: firebaseUser.photoURL || '',
        provider: 'google'
      };

      let res;
      try {
        res = await api.user.post('/users/google', googleUserData);
      } catch (e) {}
      if (!res || !res.ok) {
        try {
          res = await api.post('/users/google', googleUserData);
        } catch (e) {}
      }

      if (res && res.ok) {
        const data = await res.json();
        const userObj = data.user || data;
        return { user: normalizeUser(userObj) };
      }
    } catch (error) {
      console.warn('Backend DynamoDB sync warning:', error.message);
    }
    return null;
  };

  // Listen for Firebase auth state changes (used for Google Sign-In)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        if (isSyncing) return;
        setIsSyncing(true);

        try {
          // Sync with DynamoDB backend
          const backendUser = await syncUserWithBackend(firebaseUser);
          if (backendUser && backendUser.user) {
            if (!backendUser.user.photoURL && firebaseUser.photoURL) {
              backendUser.user.photoURL = firebaseUser.photoURL;
            }
            const normalized = normalizeUser(backendUser.user);
            setCurrentUser(normalized);
            localStorage.setItem('currentUser', JSON.stringify(normalized));
          } else {
            // Fallback to basic user object
            const basicUser = normalizeUser({
              id: firebaseUser.uid,
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              firstName: firebaseUser.displayName?.split(' ')[0] || '',
              lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
              displayName: firebaseUser.displayName || firebaseUser.email,
              photoURL: firebaseUser.photoURL || '',
              role: isUserAdmin({ email: firebaseUser.email }) ? 'admin' : 'member'
            });
            setCurrentUser(basicUser);
            localStorage.setItem('currentUser', JSON.stringify(basicUser));
          }
        } catch (error) {
          console.error('Error syncing user with DynamoDB:', error);
        } finally {
          setIsSyncing(false);
          setLoading(false);
        }
      } else {
        // If not a Firebase session, check if there is an active DynamoDB email session in localStorage
        const cachedUserStr = localStorage.getItem('currentUser');
        if (cachedUserStr) {
          try {
            const parsed = JSON.parse(cachedUserStr);
            if (parsed && parsed.email) {
              const normalized = normalizeUser(parsed);
              setCurrentUser(normalized);
              setLoading(false);
              return;
            }
          } catch (e) {}
        }

        setCurrentUser(null);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('authToken');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Periodic role check from DynamoDB backend (every 30 seconds)
  useEffect(() => {
    if (!currentUser?.email) return;
    
    // Immediate initial role refresh
    refreshUserRole(currentUser);

    const interval = setInterval(async () => {
      if (document.visibilityState === 'visible') {
        await refreshUserRole(currentUser);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [currentUser?.email]);

  // Google Sign-In via Firebase popup -> synchronized directly into DynamoDB
  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const backendUser = await syncUserWithBackend(result.user);
      const user = normalizeUser(backendUser?.user || result.user);
      setCurrentUser(user);
      localStorage.setItem('currentUser', JSON.stringify(user));
      return user;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Email/password login: Authenticates 100% against DynamoDB backend
  const signInWithEmail = async (email, password) => {
    setLoading(true);
    const cleanEmail = email.trim().toLowerCase();

    try {
      let res;
      try {
        res = await api.user.post('/users/login', { email: cleanEmail, password });
      } catch (e) {}
      if (!res || !res.ok) {
        try {
          res = await api.post('/users/login', { email: cleanEmail, password });
        } catch (e) {}
      }
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.message || 'Invalid email or password');
      }

      const normalized = normalizeUser(data.user);
      setCurrentUser(normalized);
      localStorage.setItem('currentUser', JSON.stringify(normalized));
      const token = `token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('authToken', token);

      return normalized;
    } catch (err) {
      console.error('Login error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Email/password registration: Stores user 100% in DynamoDB (bow-users table)
  const registerWithEmail = async ({ email, password, firstName, lastName, phone }) => {
    setLoading(true);
    const cleanEmail = email.trim().toLowerCase();
    const cleanFirstName = firstName.trim();
    const cleanLastName = lastName.trim();
    const cleanPhone = (phone || '').trim();

    try {
      let res;
      try {
        res = await api.user.post('/users/register', { 
          email: cleanEmail, 
          password, 
          firstName: cleanFirstName, 
          lastName: cleanLastName, 
          phone: cleanPhone 
        });
      } catch (e) {}
      if (!res || !res.ok) {
        try {
          res = await api.post('/users/register', { 
            email: cleanEmail, 
            password, 
            firstName: cleanFirstName, 
            lastName: cleanLastName, 
            phone: cleanPhone 
          });
        } catch (e) {}
      }
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      const normalized = normalizeUser(data.user);
      setCurrentUser(normalized);
      localStorage.setItem('currentUser', JSON.stringify(normalized));
      const token = `token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('authToken', token);

      return normalized;
    } catch (err) {
      console.error('Registration error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
    }
    
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken');
  };

  // Refresh user role from DynamoDB database
  const refreshUserRole = async (targetUser = currentUser) => {
    if (!targetUser?.email) return targetUser;
    
    try {
      let res;
      try {
        res = await api.user.get(`/users/refresh-role/${encodeURIComponent(targetUser.email)}`);
      } catch (e) {}
      if (!res || !res.ok) {
        try {
          res = await api.get(`/users/refresh-role/${encodeURIComponent(targetUser.email)}`);
        } catch (e) {}
      }

      if (res && res.ok) {
        const data = await res.json();
        if (data.user) {
          const normalized = normalizeUser({ ...targetUser, ...data.user });
          if (normalized.role !== targetUser.role) {
            setCurrentUser(normalized);
            localStorage.setItem('currentUser', JSON.stringify(normalized));
            return normalized;
          }
        }
      }
      return targetUser;
    } catch (error) {
      return targetUser;
    }
  };

  // Update user profile in DynamoDB database
  const updateUserProfile = async (uidOrEmail, updates) => {
    try {
      const identifier = uidOrEmail || currentUser?.uid || currentUser?.id || currentUser?.email;
      if (!identifier) throw new Error('No user identifier available for profile update');

      let res;
      try {
        res = await api.user.put(`/users/${encodeURIComponent(identifier)}`, updates);
      } catch (e) {}
      if (!res || !res.ok) {
        try {
          res = await api.put(`/users/${encodeURIComponent(identifier)}`, updates);
        } catch (e) {}
      }

      const data = await res.json();
      if (!res || !res.ok) {
        throw new Error(data.message || 'Failed to update profile in database');
      }

      const updatedUser = normalizeUser({
        ...currentUser,
        ...(data.user || updates)
      });
      setCurrentUser(updatedUser);
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    signInWithGoogle,
    signInWithEmail,
    registerWithEmail,
    updateUserProfile,
    signOut,
    loading,
    refreshUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};